import { HttpClient } from '@wix/http-client';
import EventEmitter from 'events';
import { v4 as uuid } from 'uuid';
import { WIDGET_SDK_CHANNEL, WidgetEvents } from './types';
const trustedSourcesRegexp = /^https:\/\/(.+)\.(wix|editorx)\.com$/i;
const httpClient = new HttpClient();
const DEFAULT_API_TIMEOUT = 5000;
export class WidgetSDK extends EventEmitter {
  constructor(apiTimeout) {
    if (apiTimeout === void 0) {
      apiTimeout = DEFAULT_API_TIMEOUT;
    }
    super();
    this._requests = new Map();
    this.apiTimeout = void 0;
    this.subscribeToContextUpdate = postUrl => {
      const updateContext = context => {
        try {
          httpClient.post(postUrl, {
            context
          });
        } catch (err) {
          console.error(err);
        }
      };
      this.addListener(WidgetEvents.CONTEXT_CHANGE, updateContext);
      return () => this.removeListener(WidgetEvents.CONTEXT_CHANGE, updateContext);
    };
    this.getContext = _parentUrl => {
      return this.executeInternalMethod({
        methodName: 'getContext'
      });
    };
    this.getWidgetPlacement = () => {
      return this.executeInternalMethod({
        methodName: 'getWidgetPlacement'
      });
    };
    this.getOrigParams = () => {
      return this.executeInternalMethod({
        methodName: 'getOrigParams'
      });
    };
    this.getInitialParams = () => {
      return this.executeInternalMethod({
        methodName: 'getInitialParams'
      });
    };
    this.getIsMobileView = () => {
      return this.executeInternalMethod({
        methodName: 'getIsMobileView'
      });
    };
    this.getRuntimeState = () => {
      return this.executeInternalMethod({
        methodName: 'getRuntimeState'
      }, this.apiTimeout);
    };
    this.refreshRuntimeState = () => {
      return this.executeInternalMethod({
        methodName: 'refreshRuntimeState'
      }, this.apiTimeout);
    };
    this.setWidgetSize = params => {
      return this.executeInternalMethod({
        methodName: 'setWidgetSize',
        methodParams: params
      });
    };
    this.getWidgetSize = () => {
      return this.executeInternalMethod({
        methodName: 'getWidgetSize'
      });
    };
    // TODO: remove it when merging chatbot-client artifact
    /** @deprecated */
    this.getHomepageContent = () => {
      throw new Error('Method not implemented.');
    };
    this.navigateTo = payload => {
      return this.executeMethod({
        methodName: 'navigateTo',
        methodParams: payload
      }, this.apiTimeout);
    };
    this.collapseWidget = closingTrigger => {
      return this.executeMethod({
        methodName: 'collapseWidget',
        methodParams: closingTrigger
      });
    };
    this.isWidgetExpanded = () => {
      return this.executeMethod({
        methodName: 'isWidgetExpanded'
      });
    };
    this.clientAppLoaded = () => {
      return this.executeInternalMethod({
        methodName: 'clientAppLoaded'
      });
    };
    this.refreshOnUserLogin = () => {
      return this.executeInternalMethod({
        methodName: 'refreshOnUserLogin'
      }, this.apiTimeout);
    };
    this.getStartChatParams = () => {
      return this.executeInternalMethod({
        methodName: 'getStartChatParams'
      }, this.apiTimeout);
    };
    this.apiTimeout = apiTimeout;
    this.init();
  }
  init() {
    // handle reverse post message from widget and fires event which is handled in executeMethod
    window.addEventListener('message', _ref => {
      let {
        origin,
        data,
        source
      } = _ref;
      if (
      // to simulate messages from parent in tests
      !trustedSourcesRegexp.test(origin) && source !== window || (data == null ? void 0 : data.type) !== WIDGET_SDK_CHANNEL) {
        return;
      }
      // emits event to be caught in executeMethod listener
      this.emit(data.event, data.payload);
    });
    this.on(WidgetEvents.CALL_SDK_METHOD_RESPONSE, _ref2 => {
      let {
        requestId,
        result
      } = _ref2;
      return this.resolveRequest(requestId, result);
    });
    this.on(WidgetEvents.CALL_INTERNAL_SDK_METHOD_RESPONSE, _ref3 => {
      let {
        requestId,
        result
      } = _ref3;
      return this.resolveRequest(requestId, result);
    });
  }
  executeMethod(_ref4, rejectTimeoutMs) {
    let {
      methodName,
      methodParams
    } = _ref4;
    if (rejectTimeoutMs === void 0) {
      rejectTimeoutMs = 800;
    }
    const requestId = uuid();
    return Promise.race([new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error(`WidgetSDK.executeMethod(${methodName}): response event did not come`));
        this._requests.delete(requestId);
      }, rejectTimeoutMs);
    }), new Promise(resolve => {
      window.parent.postMessage({
        data: {
          event: WidgetEvents.CALL_SDK_METHOD_REQUEST,
          payload: {
            requestId,
            methodName,
            methodParams
          }
        },
        type: WIDGET_SDK_CHANNEL
      });
      this._requests.set(requestId, resolve);
    })]);
  }
  executeInternalMethod(_ref5, rejectTimeoutMs) {
    let {
      methodName,
      methodParams
    } = _ref5;
    if (rejectTimeoutMs === void 0) {
      rejectTimeoutMs = 500;
    }
    const requestId = uuid();
    return Promise.race([new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error(`WidgetSDK.executeInternalMethod(${methodName}): response event did not come`));
        this._requests.delete(requestId);
      }, rejectTimeoutMs);
    }), new Promise(resolve => {
      // send post message to widget
      window.parent.postMessage({
        data: {
          event: WidgetEvents.CALL_INTERNAL_SDK_METHOD_REQUEST,
          payload: {
            requestId,
            methodName,
            methodParams
          }
        },
        type: WIDGET_SDK_CHANNEL
      });
      this._requests.set(requestId, resolve);
    })]);
  }
  resolveRequest(requestId, result) {
    const resolve = this._requests.get(requestId);
    if (resolve) {
      resolve(result);
      this._requests.delete(requestId);
    } else {
      new Error(`WidgetSDK.resolveRequest: request with this Id is not found or rejected`);
    }
  }
}
//# sourceMappingURL=WidgetSDK.js.map